import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { Icon } from '@iconify/react/dist/iconify.js';
import api from '../../services/api';
import { toast } from 'react-toastify';
import ViewDeletionRequestModal from './ViewDeletionRequestModal';

const DeletionRequestsTable = () => {
  const [data, setData] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [selectedResourceType, setSelectedResourceType] = useState('');
  const [currentRequest, setCurrentRequest] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Fetch Deletion Requests from API
  const fetchDeletionRequests = async () => {
    try {
      const response = await api.get('/deletionRequests/');
      // Assuming the API returns the array in response.data.data
      setData(response.data.data);
    } catch (error) {
      console.error('Error fetching deletion requests:', error);
    }
  };

  useEffect(() => {
    fetchDeletionRequests();
  }, []);

  // Map the raw resource_type to a more readable name
  const resourceTypeName = (row) => {
    if (row.resource_type === 'App\\Models\\Project') return 'Project';
    if (row.resource_type === 'App\\Models\\Unit') return 'Unit';
    if (row.resource_type === 'App\\Models\\PaymentPlan') return 'Payment Plan';
    return 'Developer';
  };

  // Approve Request
  const handleApprove = async (request) => {
    try {
      await api.post(`/deletionRequests/${request.id}/approve`);
      toast.success('Request Approved Successfully!');
      // Refresh table data
      fetchDeletionRequests();
    } catch (error) {
      if (request.resource_type === 'App\\Models\\PaymentPlan') {
        toast.error(
          'Failed to Approve Request. The user must set another payment plan before deletion.'
        );
      } else {
        toast.error('Failed to Approve Request');
      }
      console.error(error);
    }
  };

  // Decline Request
  const handleDecline = async (request) => {
    try {
      await api.post(`/deletionRequests/${request.id}/reject`);
      toast.success('Request Declined Successfully!');
      // Refresh table data
      fetchDeletionRequests();
    } catch (error) {
      toast.error('Failed to Decline Request');
      console.error(error);
    }
  };

  // View (opens the modal)
  const handleView = (request) => {
    setCurrentRequest(request);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setCurrentRequest(null);
    // Re-fetch if changes might have occurred
    fetchDeletionRequests();
  };

  // Build unique resource type options for the dropdown filter
  const resourceTypes = Array.from(new Set(data.map((item) => resourceTypeName(item))));

  // Filter the data based on text search (by ID) and selected resource type
  const filteredData = data.filter((item) => {
    const matchesText = filterText ? String(item.id).includes(filterText) : true;
    const matchesResource = selectedResourceType
      ? resourceTypeName(item) === selectedResourceType
      : true;
    return matchesText && matchesResource;
  });

  // CSV export headers
  const csvHeaders = [
    { label: 'Request ID', key: 'id' },
    { label: 'Developer Sales', key: 'user.name' },
    { label: 'Email', key: 'user.email' },
    { label: 'Resource Type', key: 'resource_type' },
  ];

  // Define DataTable columns
  const columns = [
    {
      name: 'Request ID',
      selector: (row) => row.id,
      width: '120px',
      sortable: true,
    },
    {
      name: 'Developer Sales',
      selector: (row) => row?.user?.name || '',
      sortable: true,
    },
    {
      name: 'Email',
      selector: (row) => row?.user?.email || '',
      sortable: true,
    },
    {
      name: 'Resource Type',
      selector: (row) => resourceTypeName(row),
      width: '150px',
      sortable: true,
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div className="d-flex">
          <button
            onClick={() => handleView(row)}
            title="View"
            className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
          >
            <Icon icon="iconamoon:eye-light" />
          </button>
          <button
            onClick={() => handleApprove(row)}
            title="Approve"
            className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
          >
            <Icon icon="mdi:check-circle-outline" />
          </button>
          <button
            onClick={() => handleDecline(row)}
            title="Decline"
            className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
          >
            <Icon icon="mdi:close-circle-outline" />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  // Make text a bit larger via custom row styling
  const customStyles = {
    rows: {
      style: {
        fontSize: '15px', // Adjust as needed
      },
    },
  };

  return (
    <div className="card basic-data-table px-3 py-3">
      <div className="card-body">

        {/* Filters and Export */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex gap-2">
            {/* Text Filter (by ID) */}
            <input
              type="text"
              placeholder="Search by ID"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="form-control"
              style={{ width: '200px' }}
            />
            {/* Resource Type Filter */}
            <select
              value={selectedResourceType}
              onChange={(e) => setSelectedResourceType(e.target.value)}
              className="form-control"
              style={{ width: '200px' }}
            >
              <option value="">All Resource Types</option>
              {resourceTypes.map((rt) => (
                <option key={rt} value={rt}>{rt}</option>
              ))}
            </select>
          </div>

          {/* CSV Export */}
          <CSVLink
            data={filteredData}
            headers={csvHeaders}
            filename="deletion-requests.csv"
            className="btn btn-secondary"
          >
            Export CSV
          </CSVLink>
        </div>

        {/* DataTable */}
        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          highlightOnHover
          responsive
          striped
          customStyles={customStyles}
        />
      </div>

      {/* View Modal */}
      {showViewModal && (
        <ViewDeletionRequestModal
          show={showViewModal}
          handleClose={handleCloseViewModal}
          deletionRequest={currentRequest}
        />
      )}
    </div>
  );
};

export default DeletionRequestsTable;
